import { useCallback, useState } from "react";
import { supabase } from "../supabaseClient";

export function useVocabulary() {
  const [vocabularyTopics, setVocabularyTopics] = useState([]);
  const [vocabularyTerms, setVocabularyTerms] = useState([]);
  const [vocabularyTranslations, setVocabularyTranslations] = useState([]);
  const [vocabularyTermTopics, setVocabularyTermTopics] = useState([]);

  const [loadingVocabulary, setLoadingVocabulary] = useState(false);

  const [vocabularyUserProgress, setVocabularyUserProgress] = useState([]);
  const [submittingVocabularyReview, setSubmittingVocabularyReview] =
    useState(false);

  const [dueVocabularyTerms, setDueVocabularyTerms] = useState([]);
  const [loadingDueVocabulary, setLoadingDueVocabulary] = useState(false);

  const [vocabularyDifficultyStats, setVocabularyDifficultyStats] = useState([]);

  const fetchVocabularyTopics = async () => {
    setLoadingVocabulary(true);

    const { data, error } = await supabase
      .from("vocabulary_topics")
      .select("*")
      .eq("status", "active")
      .order("display_order", { ascending: true });

    if (error) {
      console.error("FETCH VOCAB TOPICS ERROR:", {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
      });

      setLoadingVocabulary(false);
      return [];
    }

    setVocabularyTopics(data || []);
    setLoadingVocabulary(false);

    return data || [];
  };
  
  const fetchVocabularyProgressForTerms = async (termIds = []) => {
    if (!termIds.length) {
      setVocabularyUserProgress([]);
      return [];
    }

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user) {
        setVocabularyUserProgress([]);
        return [];
      }

      const { data, error } = await supabase
        .from("vocabulary_user_progress")
        .select("*")
        .eq("user_id", user.id)
        .in("term_id", termIds);

      if (error) {
        throw error;
      }

      setVocabularyUserProgress(data || []);

      return data || [];
    } catch (error) {
      console.error(
        "FETCH VOCABULARY PROGRESS ERROR:",
        error
      );

      setVocabularyUserProgress([]);

      return [];
    }
  };

  const fetchDueVocabularyTerms = async () => {
    setLoadingDueVocabulary(true);

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user) {
        setDueVocabularyTerms([]);
        setLoadingDueVocabulary(false);
        return [];
      }

      const now = new Date().toISOString();

      // 1. 找当前用户所有到期 progress
      const { data: progressRows, error: progressError } =
        await supabase
          .from("vocabulary_user_progress")
          .select("*")
          .eq("user_id", user.id)
          .lte("next_review_at", now)
          .not("next_review_at", "is", null)
          .order("next_review_at", { ascending: true });

      if (progressError) {
        throw progressError;
      }

      const termIds = (progressRows || []).map(
        (row) => row.term_id
      );

      if (termIds.length === 0) {
        setDueVocabularyTerms([]);
        setLoadingDueVocabulary(false);
        return [];
      }

      // 2. 拉 vocabulary terms
      const { data: terms, error: termsError } = await supabase
        .from("vocabulary_terms")
        .select("*")
        .in("id", termIds)
        .eq("status", "active");

      if (termsError) {
        throw termsError;
      }

      // 3. 拉 translations
      const { data: translations, error: translationsError } =
        await supabase
          .from("vocabulary_translations")
          .select("*")
          .in("term_id", termIds);

      if (translationsError) {
        throw translationsError;
      }

      /*
        这里我们继续复用现有 vocabularyTranslations，
        这样 getTranslationsForTerm() 仍然可以工作。
      */
      setVocabularyTranslations(translations || []);

      // 按 next_review_at 顺序整理 terms
      const progressMap = new Map(
        (progressRows || []).map((row) => [
          row.term_id,
          row,
        ])
      );

      const orderedTerms = [...(terms || [])].sort((a, b) => {
        const aDate =
          progressMap.get(a.id)?.next_review_at || "";

        const bDate =
          progressMap.get(b.id)?.next_review_at || "";

        return new Date(aDate) - new Date(bDate);
      });

      setDueVocabularyTerms(orderedTerms);
      setLoadingDueVocabulary(false);

      return orderedTerms;
    } catch (error) {
      console.error(
        "FETCH DUE VOCABULARY ERROR:",
        error
      );

      setDueVocabularyTerms([]);
      setLoadingDueVocabulary(false);

      return [];
    }
  };

  const fetchVocabularyDifficultyStats = async (termIds = []) => {
    if (!termIds.length) {
      setVocabularyDifficultyStats([]);
      return [];
    }

    try {
      const { data, error } = await supabase.rpc(
        "get_vocabulary_term_difficulty_stats",
        {
          p_term_ids: termIds,
        }
      );

      if (error) {
        throw error;
      }

      setVocabularyDifficultyStats(data || []);

      return data || [];
    } catch (error) {
      console.error(
        "FETCH VOCABULARY DIFFICULTY STATS ERROR:",
        error
      );

      setVocabularyDifficultyStats([]);

      return [];
    }
  };

  const fetchVocabularyByTopic = async (topicId) => {
    if (!topicId) return [];

    setLoadingVocabulary(true);

    try {
      const { data: mappings, error: mappingError } = await supabase
        .from("vocabulary_term_topics")
        .select("*")
        .eq("topic_id", topicId);

      if (mappingError) {
        throw mappingError;
      }

      const termIds = (mappings || []).map((row) => row.term_id);

      if (termIds.length === 0) {
        setVocabularyTerms([]);
        setVocabularyTranslations([]);
        setVocabularyTermTopics(mappings || []);
        setLoadingVocabulary(false);
        return [];
      }

      const { data: terms, error: termsError } = await supabase
        .from("vocabulary_terms")
        .select("*")
        .in("id", termIds)
        .eq("status", "active")
        .order("english_term", { ascending: true });

      if (termsError) {
        throw termsError;
      }

      const { data: translations, error: translationsError } = await supabase
        .from("vocabulary_translations")
        .select("*")
        .in("term_id", termIds);

      if (translationsError) {
        throw translationsError;
      }

      const loadedTerms = terms || [];

      setVocabularyTermTopics(mappings || []);
      setVocabularyTerms(loadedTerms);
      setVocabularyTranslations(translations || []);

      await fetchVocabularyProgressForTerms(
        loadedTerms.map((term) => term.id)
      );

      await fetchVocabularyDifficultyStats(
        loadedTerms.map((term) => term.id)
      );

      setLoadingVocabulary(false);

      return terms || [];
    } catch (error) {
      console.error("FETCH VOCABULARY BY TOPIC ERROR:", error);
      setLoadingVocabulary(false);
      return [];
    }
  };

  const submitVocabularyMatchingResult = async ({
    termId,
    sessionId,
    wrongAttempts = 0,
  }) => {
    if (!termId || !sessionId) {
      return {
        success: false,
        error: "Missing termId or sessionId.",
      };
    }

    const firstTryCorrect = wrongAttempts === 0;

    try {
      // =========================================================
      // 1. GET CURRENT USER
      // =========================================================

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user) {
        throw new Error("No authenticated user.");
      }

      // =========================================================
      // 2. GET CURRENT USER PROGRESS
      // =========================================================

      const { data: existingProgress, error: progressError } =
        await supabase
          .from("vocabulary_user_progress")
          .select("*")
          .eq("user_id", user.id)
          .eq("term_id", termId)
          .maybeSingle();

      if (progressError) {
        throw progressError;
      }

      const currentReviewCount =
        existingProgress?.review_count || 0;

      const currentCorrectCount =
        existingProgress?.correct_count || 0;

      const currentMastery =
        existingProgress?.mastery_level || 0;

      const newReviewCount =
        currentReviewCount + 1;

      const newCorrectCount =
        currentCorrectCount +
        (firstTryCorrect ? 1 : 0);

      // =========================================================
      // 3. MASTERY
      //
      // Matching:
      // first try correct -> +1 mastery
      // wrong first      -> no mastery increase
      //
      // Never above 3.
      // =========================================================

      const newMasteryLevel = firstTryCorrect
        ? Math.min(currentMastery + 1, 3)
        : currentMastery;

      const now = new Date().toISOString();

      // =========================================================
      // 4. UPDATE / INSERT USER PROGRESS
      // =========================================================

      const progressPayload = {
        user_id: user.id,
        term_id: termId,

        mastery_level: newMasteryLevel,

        review_count: newReviewCount,
        correct_count: newCorrectCount,

        last_reviewed_at: now,

        // Matching does NOT change the existing review schedule yet.
        next_review_at:
          existingProgress?.next_review_at || null,

        is_favorite:
          existingProgress?.is_favorite || false,

        updated_at: now,
      };

      let progressSaveError;

      if (existingProgress) {
        const { error } = await supabase
          .from("vocabulary_user_progress")
          .update(progressPayload)
          .eq("id", existingProgress.id);

        progressSaveError = error;
      } else {
        const { error } = await supabase
          .from("vocabulary_user_progress")
          .insert({
            ...progressPayload,
            created_at: now,
          });

        progressSaveError = error;
      }

      if (progressSaveError) {
        throw progressSaveError;
      }

      // =========================================================
      // 5. INSERT MATCHING ATTEMPT
      // =========================================================

      const { error: attemptError } = await supabase
        .from("vocabulary_review_attempts")
        .insert({
          user_id: user.id,
          term_id: termId,
          activity_type: "matching",
          activity_session_id: sessionId,
          is_correct: firstTryCorrect,
          result: firstTryCorrect
            ? "first_try_correct"
            : "eventually_correct",
          response_data: {
            wrong_attempts: wrongAttempts,
            first_try_correct: firstTryCorrect,
          },
        });

      if (attemptError) {
        throw attemptError;
      }

      return {
        success: true,

        termId,
        firstTryCorrect,
        wrongAttempts,

        masteryLevel: newMasteryLevel,
        reviewCount: newReviewCount,
        correctCount: newCorrectCount,
      };
    } catch (error) {
      console.error(
        "SUBMIT VOCABULARY MATCHING RESULT ERROR:",
        error
      );

      return {
        success: false,
        error:
          error?.message ||
          "Failed to save matching result.",
      };
    }
  };

  const submitVocabularyQuizResult = async ({
    termId,
    sessionId,
    isCorrect,
    selectedTermId = null,
  }) => {
    if (!termId || !sessionId) {
      return {
        success: false,
        error: "Missing termId or sessionId.",
      };
    }

    try {
      // =========================================================
      // 1. GET CURRENT USER
      // =========================================================

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user) {
        throw new Error("No authenticated user.");
      }

      // =========================================================
      // 2. GET EXISTING USER PROGRESS
      // =========================================================

      const {
        data: existingProgress,
        error: progressError,
      } = await supabase
        .from("vocabulary_user_progress")
        .select("*")
        .eq("user_id", user.id)
        .eq("term_id", termId)
        .maybeSingle();

      if (progressError) {
        throw progressError;
      }

      const currentReviewCount =
        existingProgress?.review_count || 0;

      const currentCorrectCount =
        existingProgress?.correct_count || 0;

      const currentMastery =
        existingProgress?.mastery_level || 0;

      // =========================================================
      // 3. CALCULATE NEW PROGRESS
      // =========================================================

      const newReviewCount =
        currentReviewCount + 1;

      const newCorrectCount =
        currentCorrectCount +
        (isCorrect ? 1 : 0);

      const newMasteryLevel = isCorrect
        ? Math.min(currentMastery + 1, 3)
        : Math.max(currentMastery - 1, 0);

      // =========================================================
      // 4. CALCULATE NEXT REVIEW
      // =========================================================

      const nowDate = new Date();
      const nextReviewDate = new Date(nowDate);

      if (isCorrect) {
        nextReviewDate.setDate(
          nextReviewDate.getDate() + 3
        );
      } else {
        nextReviewDate.setMinutes(
          nextReviewDate.getMinutes() + 10
        );
      }

      const now = nowDate.toISOString();

      const nextReviewAt =
        nextReviewDate.toISOString();

      // =========================================================
      // 5. SAVE USER PROGRESS
      // =========================================================

      const progressPayload = {
        user_id: user.id,
        term_id: termId,

        mastery_level: newMasteryLevel,

        review_count: newReviewCount,
        correct_count: newCorrectCount,

        last_reviewed_at: now,
        next_review_at: nextReviewAt,

        is_favorite:
          existingProgress?.is_favorite || false,

        updated_at: now,
      };

      let savedProgress;

      if (existingProgress) {
        const { data, error } = await supabase
          .from("vocabulary_user_progress")
          .update(progressPayload)
          .eq("id", existingProgress.id)
          .select()
          .single();

        if (error) {
          throw error;
        }

        savedProgress = data;
      } else {
        const { data, error } = await supabase
          .from("vocabulary_user_progress")
          .insert({
            ...progressPayload,
            created_at: now,
          })
          .select()
          .single();

        if (error) {
          throw error;
        }

        savedProgress = data;
      }

      // =========================================================
      // 6. SAVE QUIZ ATTEMPT
      // =========================================================

      const { error: attemptError } = await supabase
        .from("vocabulary_review_attempts")
        .insert({
          user_id: user.id,
          term_id: termId,

          activity_type: "quiz",
          activity_session_id: sessionId,

          is_correct: isCorrect,

          result: isCorrect
            ? "correct"
            : "incorrect",

          response_data: {
            selected_term_id: selectedTermId,
            correct_term_id: termId,
          },

          // reviewed_at already defaults to now()
          // so we do NOT insert created_at here.
        });

      if (attemptError) {
        throw attemptError;
      }

      // =========================================================
      // 7. UPDATE LOCAL REACT PROGRESS
      // =========================================================

      setVocabularyUserProgress((prev) => {
        const exists = prev.some(
          (row) => row.term_id === termId
        );

        if (exists) {
          return prev.map((row) =>
            row.term_id === termId
              ? savedProgress
              : row
          );
        }

        return [...prev, savedProgress];
      });

      return {
        success: true,

        data: savedProgress,

        termId,
        isCorrect,

        masteryLevel: newMasteryLevel,
        reviewCount: newReviewCount,
        correctCount: newCorrectCount,

        nextReviewAt,
      };
    } catch (error) {
      console.error(
        "SUBMIT VOCABULARY QUIZ RESULT ERROR:",
        error
      );

      return {
        success: false,
        error:
          error?.message ||
          "Failed to save vocabulary quiz result.",
      };
    }
  };

  const searchVocabulary = async (searchText) => {
    const keyword = searchText?.trim();

    if (!keyword) {
      return [];
    }

    setLoadingVocabulary(true);

    try {
      // Search English terms
      const { data: englishTerms, error: englishError } = await supabase
        .from("vocabulary_terms")
        .select("*")
        .eq("status", "active")
        .ilike("english_term", `%${keyword}%`)
        .limit(100);

      if (englishError) {
        throw englishError;
      }

      // Search translated terms
      const { data: translationMatches, error: translationError } =
        await supabase
          .from("vocabulary_translations")
          .select("*")
          .ilike("translation", `%${keyword}%`)
          .limit(100);

      if (translationError) {
        throw translationError;
      }

      const englishIds = (englishTerms || []).map((term) => term.id);

      const translatedIds = (translationMatches || []).map(
        (row) => row.term_id
      );

      const uniqueTermIds = [
        ...new Set([...englishIds, ...translatedIds]),
      ];

      if (uniqueTermIds.length === 0) {
        setLoadingVocabulary(false);
        return [];
      }

      const { data: finalTerms, error: finalTermsError } = await supabase
        .from("vocabulary_terms")
        .select("*")
        .in("id", uniqueTermIds)
        .eq("status", "active")
        .order("english_term", { ascending: true });

      if (finalTermsError) {
        throw finalTermsError;
      }

      const { data: finalTranslations, error: finalTranslationsError } =
        await supabase
          .from("vocabulary_translations")
          .select("*")
          .in("term_id", uniqueTermIds);

      if (finalTranslationsError) {
        throw finalTranslationsError;
      }

      setVocabularyTerms(finalTerms || []);
      setVocabularyTranslations(finalTranslations || []);

      setLoadingVocabulary(false);

      return finalTerms || [];
    } catch (error) {
      console.error("SEARCH VOCABULARY ERROR:", error);
      setLoadingVocabulary(false);
      return [];
    }
  };

  const getTranslationsForTerm = useCallback(
    (termId) => {
      const rows = vocabularyTranslations.filter(
        (row) => row.term_id === termId
      );

      return {
        zh:
          rows.find(
            (row) =>
              row.language_code === "zh"
          )?.translation || "",

        es:
          rows.find(
            (row) =>
              row.language_code === "es"
          )?.translation || "",

        ru:
          rows.find(
            (row) =>
              row.language_code === "ru"
          )?.translation || "",
      };
    },
    [vocabularyTranslations]
  );

  const calculateNextReviewAt = (rating) => {
    const now = new Date();
    const nextReview = new Date(now);

    switch (rating) {
      case "again":
        nextReview.setMinutes(
          nextReview.getMinutes() + 10
        );
        break;

      case "hard":
        nextReview.setDate(
          nextReview.getDate() + 1
        );
        break;

      case "good":
        nextReview.setDate(
          nextReview.getDate() + 3
        );
        break;

      case "easy":
        nextReview.setDate(
          nextReview.getDate() + 7
        );
        break;

      default:
        return null;
    }

    return nextReview.toISOString();
  };

  const submitVocabularyReview = async (termId, rating) => {
    if (!termId || !rating) {
      return {
        success: false,
        error: "Missing termId or rating.",
      };
    }

    const ratingConfig = {
      again: {
        masteryLevel: 0,
        isCorrect: false,
      },
      hard: {
        masteryLevel: 1,
        isCorrect: false,
      },
      good: {
        masteryLevel: 2,
        isCorrect: true,
      },
      easy: {
        masteryLevel: 3,
        isCorrect: true,
      },
    };

    const config = ratingConfig[rating];

    if (!config) {
      return {
        success: false,
        error: `Unsupported rating: ${rating}`,
      };
    }

    const nextReviewAt = calculateNextReviewAt(rating);

    setSubmittingVocabularyReview(true);

    try {
      // ---------------------------------------------------------
      // 1. Get current logged-in user
      // ---------------------------------------------------------

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user) {
        throw new Error("No authenticated user found.");
      }

      // ---------------------------------------------------------
      // 2. Check whether progress already exists
      // ---------------------------------------------------------

      const { data: existingProgress, error: progressError } =
        await supabase
          .from("vocabulary_user_progress")
          .select("*")
          .eq("user_id", user.id)
          .eq("term_id", termId)
          .maybeSingle();

      if (progressError) {
        throw progressError;
      }

      let savedProgress;

      // ---------------------------------------------------------
      // 3A. Existing word -> UPDATE
      // ---------------------------------------------------------

      if (existingProgress) {
        const newReviewCount =
          (existingProgress.review_count || 0) + 1;

        const newCorrectCount =
          (existingProgress.correct_count || 0) +
          (config.isCorrect ? 1 : 0);

        const { data, error } = await supabase
          .from("vocabulary_user_progress")
          .update({
            mastery_level: config.masteryLevel,
            review_count: newReviewCount,
            correct_count: newCorrectCount,
            last_reviewed_at: new Date().toISOString(),
            next_review_at: nextReviewAt,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingProgress.id)
          .select()
          .single();

        if (error) {
          throw error;
        }

        savedProgress = data;
      }

      // ---------------------------------------------------------
      // 3B. First time seeing word -> INSERT
      // ---------------------------------------------------------

      else {
        const { data, error } = await supabase
          .from("vocabulary_user_progress")
          .insert({
            user_id: user.id,
            term_id: termId,
            mastery_level: config.masteryLevel,
            review_count: 1,
            correct_count: config.isCorrect ? 1 : 0,
            last_reviewed_at: new Date().toISOString(),
            next_review_at: nextReviewAt,
            is_favorite: false,
          })
          .select()
          .single();

        if (error) {
          throw error;
        }

        savedProgress = data;
      }

      // ---------------------------------------------------------
      // 4. Update React state
      // ---------------------------------------------------------

      setVocabularyUserProgress((prev) => {
        const exists = prev.some(
          (row) => row.term_id === termId
        );

        if (exists) {
          return prev.map((row) =>
            row.term_id === termId
              ? savedProgress
              : row
          );
        }

        return [...prev, savedProgress];
      });

      return {
        success: true,
        data: savedProgress,
      };
    } catch (error) {
      console.error(
        "SUBMIT VOCABULARY REVIEW ERROR:",
        error
      );

      return {
        success: false,
        error:
          error?.message ||
          "Unable to save vocabulary review.",
      };
    } finally {
      setSubmittingVocabularyReview(false);
    }
  };

  const getVocabularyProgressForTerm = (termId) => {
    return (
      vocabularyUserProgress.find(
        (row) => row.term_id === termId
      ) || null
    );
  };

  const getMasteryLabel = (level) => {
    switch (level) {
      case 1:
        return "Learning";
      case 2:
        return "Familiar";
      case 3:
        return "Mastered";
      default:
        return "New";
    }
  };

  const getVocabularyTopicStats = () => {
    const total = vocabularyTerms.length;

    let newCount = 0;
    let learningCount = 0;
    let familiarCount = 0;
    let masteredCount = 0;
    let dueCount = 0;

    const now = new Date();

    vocabularyTerms.forEach((term) => {
      const progress = getVocabularyProgressForTerm(
        term.id
      );

      if (!progress) {
        newCount += 1;
        return;
      }

      const level = progress.mastery_level || 0;

      if (level === 0) newCount += 1;
      if (level === 1) learningCount += 1;
      if (level === 2) familiarCount += 1;
      if (level === 3) masteredCount += 1;

      if (
        progress.next_review_at &&
        new Date(progress.next_review_at) <= now
      ) {
        dueCount += 1;
      }
    });

    const masteryPoints =
      learningCount * 1 +
      familiarCount * 2 +
      masteredCount * 3;

    const maxPoints = total * 3;

    const overallMasteryPct =
      maxPoints > 0
        ? Math.round(
            (masteryPoints / maxPoints) * 100
          )
        : 0;

    return {
      total,
      newCount,
      learningCount,
      familiarCount,
      masteredCount,
      dueCount,
      overallMasteryPct,
    };
  };

  return {
    vocabularyTopics,
    vocabularyTerms,
    vocabularyTranslations,
    vocabularyTermTopics,
    vocabularyUserProgress,

    loadingVocabulary,
    submittingVocabularyReview,

    fetchVocabularyTopics,
    fetchVocabularyByTopic,
    searchVocabulary,
    getTranslationsForTerm,

    submitVocabularyReview,
    dueVocabularyTerms,
    loadingDueVocabulary,
    fetchDueVocabularyTerms,

    fetchVocabularyProgressForTerms,
    getVocabularyProgressForTerm,
    getMasteryLabel,
    getVocabularyTopicStats,
    submitVocabularyMatchingResult,
    vocabularyDifficultyStats,
    fetchVocabularyDifficultyStats,
    submitVocabularyQuizResult,
  };
}